import React, { useCallback, useContext, useState } from 'react';
import { Box } from '@mui/material';
import { ProductInfo } from '../../ProductCards';
import { useRouter } from 'next/router';
import styles from './ProductCard.module.scss';
import classNames from 'classnames/bind';
import { Button } from 'components/baseComponents/Button';
import NoImagePlaceholder from '@assets/images/no-image-placeholder.png';
import { CartContext } from '@utils';
import { CartService } from '@services/cart';
import { getCartDraft } from '@utils/getCartDraft';
import { useServiceRef } from 'hooks';
import { Card, Typography } from 'gd-design-library';
import { ChatbotWidgetContext } from 'components/ChatbotWidget/utils/ChatbotWidgetContext';

const cx = classNames.bind(styles);

const CN = 'product-card';
const imgClass = `${CN}__img`;
const actionsClass = `${CN}__actions`;

interface ProductCardProps {
  cardContent: ProductInfo;
  scrollToBottom: () => void;
}

const getValidImageUrl = (url: string | undefined): string => {
  try {
    return new URL(url || '').href;
  } catch {
    return NoImagePlaceholder.src;
  }
};

export const ProductCard = ({ cardContent, scrollToBottom }: ProductCardProps): React.ReactElement => {
  const { cart, setCart } = useContext(CartContext);
  const { client } = useContext(ChatbotWidgetContext);
  const router = useRouter();
  const cartServiceRef = useServiceRef(CartService);
  const [ isLoading, setIsLoading ] = useState(false);

  const handleAddToCart = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const numericVariantId = Number(cardContent.variantId);
      if (!cardContent.product_ID || isNaN(numericVariantId)) return;

      const lineItemDraft = {
        productId: cardContent.product_ID,
        variantId: numericVariantId,
        quantity: 1
      };

      let updatedCart;
      if (!cart) {
        const cartDraft = getCartDraft(lineItemDraft);
        updatedCart = await cartServiceRef.current.createCart(cartDraft);
      } else {
        updatedCart = await cartServiceRef.current.addLineItem(
          cart.id,
          cart.version,
          lineItemDraft
        );
      }

      if (updatedCart) {
        setCart(updatedCart);
        if (client?.chat) {
          client.chat.sendMessage({
            type: 'text',
            content: `Add to cart: ${cardContent.title}`
          });

          scrollToBottom();
        }

      }
    } finally {
      setIsLoading(false);
    }
  }, [ cardContent, cart, setCart, cartServiceRef, client ]);

  const handleViewProduct = (): void => {
    if (cardContent.product_ID) {
      router.push(`/products/${cardContent.product_ID}`);
    }
  };

  return (
    <Card className={ cx(CN) }>
      <img
        src={ getValidImageUrl(cardContent.imageUrl) }
        className={ cx(imgClass) }
        alt={ cardContent.title }
        onLoad={ scrollToBottom }
      />
      <Typography variant='h6'>{cardContent.title}</Typography>
      <Typography>{`$${Number(cardContent.price).toFixed(2)}`}</Typography>
      <Box className={ cx(actionsClass) }>
        <Button variant='primary' onClick={ handleAddToCart } loading={ isLoading }>
          Add to Cart
        </Button>
        <Button variant='outlined' onClick={ handleViewProduct }>
          View
        </Button>
      </Box>
    </Card>
  );
};
